<template>
    <div id="archiveSearch" class="container-fluid narrow-content">
        <!-- filter -->
        <div class="bg-dark-blue mb-2" :class="{ loading: fullTextSearching }">
            <div class="px-3">
                <form class="d-flex justify-content-center align-items-center py-4 m-auto flex-wrap"
                      @submit.prevent="setSearch">
                    <div class="flex-grow-1">
                        <div class="input-group">

                            <input id="search-filter"
                                   v-model="searchFieldVal"
                                   class="form-control"
                                   type="search"
                                   :placeholder="fullTextBoxVal ? 'Search all text...' : 'Search title...'" />

                            <div class="input-group-append">
                                <div class="text-nowrap">
                                    <button class="btn btn-primary"
                                            id="search-btn"
                                            type="button"
                                            @click="setSearch">
                                        Search
                                    </button>
                                </div>
                            </div>

                            <!--<button v-show="!!searchFieldVal"
                                    class="btn btn-link ms-1"
                                    type="button"
                                    @click="clearSearch">
                                Reset
                            </button>-->
                        </div>
                    </div>

                    <div class="text-nowrap mt-2 mt-md-0">
                        <span class="ms-2 white search-all-text"
                              @click="fullTextBoxVal = !fullTextBoxVal">
                            Search all text
                            <i class="fa-regular"
                               :class="fullTextBoxVal ? 'fa-square-check' : 'fa-square'"></i>
                        </span>
                    </div>
                </form>
            </div>
        </div>

        <!-- archive results -->
        <div class="mb-5">
            <div v-if="fullTextSearching" class="d-flex justify-content-center mt-5">
                <i class="fa fa-4x fa-spinner fa-spin"></i>
            </div>

            <div :class="{ loading: fullTextSearching }">
                <div v-if="!fullTextSearching && filtered.length"
                     class="d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <small v-if="searchTerm">
                            Showing {{ filtered.length }} results for
                            <strong>{{ fullText ? "full text" : "titles" }}</strong>
                            containing <strong>{{ searchTerm }}</strong>.
                        </small>
                    </div>

                    <button class="btn btn-primary ml-2 text-nowrap"
                            type="button"
                            @click="toggleCollapseAll">
                        {{ allExpanded ? "Collapse" : "Expand" }} All
                    </button>
                </div>

                <div class="text-center" v-if="!filtered.length && !fullTextSearching">
                    <em>
                        There are no results for <strong>{{ searchTerm }}</strong>
                        included in <strong>{{ fullText ? "full text" : "title" }}</strong>.
                    </em>
                </div>

                <div class="port-accordion archives-wrapper" id="archive-results-wrapper">
                    <template v-for="year in years" :key="year">
                        <div class="title">
                            <a :href="`#Archives${year}`"
                               @click.prevent="toggleCollapse(year)"
                               :class="collapsedYears[year] ? 'collapsed' : null">
                                {{ year }}
                            </a>
                        </div>

                        <div :id="`Archives${year}`"
                             class="group"
                             v-show="!collapsedYears[year]">
                            <div class="archives-feed">
                                <div v-for="item in yearGroups[year]"
                                     :key="item.Id"
                                     class="feature-body archive-item">
                                    <div class="display-date">
                                        {{ dateFormat(item.PublishDate || item.Created) }}
                                    </div>

                                    <div class="title">
                                        <a :href="getItemUrl(item)"
                                           :target="item.ExternalUrl ? '_blank' : undefined">
                                            {{ item.Title }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import dayjs from "dayjs";
    import _ from "lodash";

    export default {
        name: "ArchiveSearch",

        props: {
            model: {
                type: Array,
                required: true
            },
            fullTextSearchUrl: {
                type: String,
                required: true
            },
            detailPageUrl: {
                type: String,
                required: true
            }
        },

        data() {
            return {
                collapsedYears: {},
                selectedCategory: null,
                searchFieldVal: "",
                searchTerm: "",
                fullTextBoxVal: false,
                fullText: false,
                fullTextSearching: false,
                fullTextMatches: null
            };
        },

        computed: {
            yearGroups() {
                return _.groupBy(this.filtered, x => dayjs(x.Created).year());
            },

            years() {
                return _(this.yearGroups)
                    .keys()
                    .map(x => parseInt(x))
                    .value()
                    .sort()
                    .reverse();
            },

            categories() {
                return _(this.model)
                    .flatMap(x => x.Categories)
                    .uniqBy(x => x.Id)
                    .value();
            },

            filtered() {
                return _.filter(this.model, x => {
                    const matchesCategory =
                        this.selectedCategory === null ||
                        _.some(x.Categories, c => c.Id === this.selectedCategory);

                    const matchesTitle =
                        !this.searchTerm ||
                        (
                            !this.fullText &&
                            x.Title?.toLowerCase().includes(this.searchTerm.toLowerCase())
                        );

                    const matchesFullText =
                        this.fullText &&
                        this.fullTextMatches?.includes(x.Id);

                    return matchesCategory && (matchesTitle || matchesFullText);
                });
            },

            allExpanded() {
                return !_.some(this.years, y => this.collapsedYears[y]);
            }
        },

        watch: {
            searchFieldVal(newVal) {
                if (!newVal) {
                    this.clearSearch();
                }
            },

            years: {
                handler(newVal) {
                    _.forEach(newVal, year => {
                        if (this.collapsedYears[year] === undefined) {
                            this.collapsedYears[year] = true;
                        }
                    });
                },
                immediate: true
            },

            searchTerm(newVal) {
                if (newVal) {
                    this.collapseOrExpandAll(false);
                }
            },

            fullTextMatches(newVal) {
                if (newVal) {
                    this.collapseOrExpandAll(false);
                }
            }
        },

        mounted() {
            const thisYear = dayjs().year();

            if (this.collapsedYears[thisYear] !== undefined) {
                this.collapsedYears[thisYear] = false;
            }
        },

        methods: {
            dateFormat(date) {
                return dayjs(date).format("M/D/YYYY");
            },

            isThisYear(year) {
                return year === dayjs().year();
            },

            toggleCollapse(year) {
                this.collapsedYears[year] = !this.collapsedYears[year];
            },

            toggleCollapseAll() {
                this.collapseOrExpandAll(this.allExpanded);
            },

            collapseOrExpandAll(collapse) {
                _.forEach(this.years, year => {
                    this.collapsedYears[year] = collapse;
                });
            },

            setSearch() {
                this.searchTerm = this.searchFieldVal;
                this.fullText = this.fullTextBoxVal;

                if (this.fullText) {
                    this.searchFullText();
                }
            },

            clearSearch() {
                this.searchFieldVal = "";
                this.setSearch();
            },

            async searchFullText() {
                this.fullTextMatches = null;

                if (!this.searchTerm) return;

                this.fullTextSearching = true;

                try {
                    const url = `${this.fullTextSearchUrl}?searchString=${encodeURIComponent(this.searchTerm)}`;
                    const response = await fetch(url);

                    if (!response.ok) {
                        throw new Error(`Full text search failed: ${response.status}`);
                    }

                    var result = await response.json();

                    this.fullTextMatches = result;
                } finally {
                    this.fullTextSearching = false;
                }
            },

            getItemUrl(item) {
                
                if (item.ExternalUrl !== "") {
                    return item.ExternalUrl;
                }                

                return `${this.detailPageUrl}/${encodeURIComponent(item.Key)}`;
            }
        }
    };
</script>